import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * The StatusMessage communicates a status with icon and text. It includes default statuses
 * like `info`, `success`, `warning` and `error`. The icon is set automatically depending on the status.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-status-message.html
 *
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {string} [label] - The label to display for the current status.
 * @param {null|'info'|'success'|'warning'|'error'} [status=null] - The current status.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {Object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The {@link StatusMessage} component.
 * @constructor
 */
export function StatusMessage({ className, label, status = null, testId, ...otherProps }) {
  const classNameList = classNames(
    'c-status-message',
    {
      [`c-status-message--${status} `]: status,
    },
    className
  ).trim()

  return (
    <div className={classNameList} data-testid={testId} {...otherProps}>
      {label}
    </div>
  )
}

StatusMessage.propTypes = {
  className: PropTypes.string, // Additional CSS classes on the component element.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
  label: PropTypes.string, // Bronson template: 'label'.
  status: PropTypes.oneOf([null, 'info', 'success', 'warning', 'error']), // Bronson template: 'status'.
}
